import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  FileText, 
  TrendingUp,
  ClipboardList,
  Target
} from "lucide-react";
import { format } from "date-fns";
import { SessionNotes } from "./SessionNotes";
import { ClientInsightsDashboard } from "./ClientInsightsDashboard";
import { ClientIntakeForm } from "./ClientIntakeForm";
import { TreatmentPlanTemplates } from "./TreatmentPlanTemplates";

interface ClientDetailProps {
  client: any;
  sessions: any[];
  onBack: () => void;
  clinicianId: string;
}

export function ClientDetail({ client, sessions, onBack, clinicianId }: ClientDetailProps) {
  const [activeTab, setActiveTab] = useState("overview");

  const clientSessions = sessions.filter(s => s.client_id === client.client_id);
  const completedSessions = clientSessions.filter(s => s.status === 'completed');
  const upcomingSessions = clientSessions
    .filter(s => new Date(s.session_date) >= new Date() && s.status !== 'cancelled')
    .sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">
            {client.client_profile?.display_name || 'Client'}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
              {client.status}
            </Badge>
            {client.therappi_connected && (
              <Badge variant="outline" className="bg-primary/10 text-primary">
                Therappi Connected
              </Badge>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total Sessions</p>
          <p className="text-2xl font-bold text-foreground">{completedSessions.length}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" className="gap-2">
            <User className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Insights
          </TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2">
            <Calendar className="h-4 w-4" />
            Sessions
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-2">
            <FileText className="h-4 w-4" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="intake" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <ClipboardList className="h-4 w-4" />
            Intake
          </TabsTrigger>
          <TabsTrigger value="treatment" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Target className="h-4 w-4" />
            Treatment Plan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-card/50 border-border/30">
              <CardHeader>
                <CardTitle className="text-lg">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client Since</span>
                  <span className="font-medium">
                    {client.accepted_at ? format(new Date(client.accepted_at), 'MMM d, yyyy') : 'Pending'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sessions Completed</span>
                  <span className="font-medium">{completedSessions.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Session</span>
                  <span className="font-medium">
                    {completedSessions.length > 0 
                      ? format(new Date(completedSessions[0].session_date), 'MMM d, yyyy')
                      : 'None yet'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-border/30">
              <CardHeader>
                <CardTitle className="text-lg">Upcoming Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingSessions.length > 0 ? (
                  <div className="space-y-2">
                    {upcomingSessions.slice(0, 3).map(session => (
                      <div key={session.id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                        <div>
                          <p className="font-medium text-sm">
                            {format(new Date(session.session_date), 'EEE, MMM d')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {session.session_time?.slice(0, 5)} • {session.duration_minutes} min
                          </p>
                        </div>
                        <Badge variant="outline">{session.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No upcoming sessions scheduled</p>
                )}
              </CardContent>
            </Card>
          </div>

          {client.notes && (
            <Card className="bg-card/50 border-border/30">
              <CardHeader>
                <CardTitle className="text-lg">Quick Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground">{client.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sessions" className="space-y-4">
          <Card className="bg-card/50 border-border/30">
            <CardHeader>
              <CardTitle className="text-lg">Session History</CardTitle>
            </CardHeader>
            <CardContent>
              {clientSessions.length > 0 ? (
                <div className="space-y-3">
                  {clientSessions.map(session => (
                    <div 
                      key={session.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/20"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">
                            {format(new Date(session.session_date), 'EEEE, MMMM d, yyyy')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {session.session_time?.slice(0, 5)} • {session.duration_minutes} min • {session.session_type}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {session.session_fee && (
                          <span className="text-sm font-medium">£{session.session_fee}</span>
                        )}
                        <Badge 
                          variant={
                            session.status === 'completed' ? 'default' :
                            session.status === 'cancelled' ? 'destructive' :
                            'outline'
                          }
                        >
                          {session.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No sessions recorded yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <SessionNotes 
            clientId={client.client_id} 
          />
        </TabsContent>

        <TabsContent value="insights">
          <ClientInsightsDashboard
            clientId={client.client_id}
            clinicianId={clinicianId}
            clientName={client.client_profile?.display_name || 'Client'}
          />
        </TabsContent>

        <TabsContent value="intake">
          <ClientIntakeForm
            clinicianId={clinicianId}
            intakeId={client.client_id}
            readOnly={true}
            onClose={() => setActiveTab('overview')}
          />
        </TabsContent>

        <TabsContent value="treatment">
          <TreatmentPlanTemplates
            clinicianId={clinicianId}
            clientId={client.client_id}
            clientName={client.client_profile?.display_name || 'Client'}
            onApplyTemplate={() => {
              // Template applied - handled by TreatmentPlanTemplates
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}